/**
 * Standalone localized-string resolver for widget-designer chrome components that
 * have no uxp context to call `$L` on — the header/rail leaves are rendered from
 * the headless editor store and never receive `uxpContext`. Prefer the real
 * `uxpContext.$L(...)` wherever the context is threaded; use this only where
 * passing it down would be invasive.
 *
 * Mirrors uxp's `localizationUtils.localize` lookup (uxp
 * `src/components/localisation/localizationUtils.ts`):
 *   - a lowercased message-code lookup against the host `window.UXPMessages`
 *     registry (the embedded strings `registerLocalization` merges in),
 *   - overlaid by DB/custom `window.ServiceDesk.__AppMessages__` overrides
 *     (already language-resolved, flat `code -> string`) which win when present,
 *   - `$Name` positional param substitution.
 *
 * Difference from the host `localize`: when the code is unregistered (e.g. the
 * standalone bundle before the host registers anything, or jsdom tests) this
 * returns the supplied English default instead of the raw code, so a missing
 * registry never renders a key. Our `localization.json` is English-only, so 'en'
 * is the correct embedded language to read.
 */

const DEFAULT_LANGUAGE = 'en';

function hasValue(v: any): boolean {
    return v !== undefined && v !== null && v !== '';
}

export function $L(code: string, fallbackEn?: string, params?: { [key: string]: any }): string {
    const fallback = hasValue(fallbackEn) ? (fallbackEn as string) : code;
    if (!hasValue(code)) return fallback;

    const lc = code.toLowerCase();
    let message: string | undefined;

    // DB/custom overrides win (flat, already language-resolved) — matching the
    // embedded-then-custom precedence in localizationUtils.buildLookupIndex.
    const custom = (window as any).ServiceDesk?.__AppMessages__ || {};
    for (const key of Object.keys(custom)) {
        if (key.toLowerCase() === lc && hasValue(custom[key])) {
            message = custom[key];
            break;
        }
    }

    // Embedded registry (registerLocalization -> window.UXPMessages), keyed by
    // code then language.
    if (!hasValue(message)) {
        const embedded = (window as any).UXPMessages || {};
        const entry = embedded[code] || embedded[lc];
        if (entry) message = entry[DEFAULT_LANGUAGE];
    }

    if (!hasValue(message)) message = fallback;

    if (params) {
        for (const key of Object.keys(params)) {
            message = (message as string).replace(`$${key}`, params[key]);
        }
    }

    return message as string;
}
